import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '../types';
import bcrypt from 'bcryptjs';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{
    error: string | null;
    requiresTwoFactor?: boolean;
    secret?: string;
    requiresEmailCode?: boolean;
    userEmail?: string;
  }>;
  verifyTwoFactor: (token: string) => Promise<{ error: string | null }>;
  verifyEmailCode: (code: string) => Promise<{ error: string | null }>;
  resendEmailCode: () => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [tempUserData, setTempUserData] = useState<User | null>(null);
  const [tempUserId, setTempUserId] = useState<string | null>(null);
  const [tempUserEmail, setTempUserEmail] = useState<string | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const sendEmailCode = async (userId: string, email: string): Promise<{ error: string | null }> => {
    try {
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString();

      const { error: updateError } = await supabase
        .from('users')
        .update({ email_2fa_code: code, email_2fa_code_expires_at: expiresAt })
        .eq('id', userId);

      if (updateError) throw updateError;

      const { error: emailError } = await supabase.functions.invoke('send-email', {
        body: {
          to: email,
          subject: 'Code de connexion K-PROD',
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 480px; margin: 0 auto; padding: 32px 24px; background: #f8fafc; border-radius: 16px;">
              <div style="text-align: center; margin-bottom: 24px;">
                <div style="display: inline-block; background: linear-gradient(135deg, #6366f1, #4338ca); border-radius: 12px; padding: 12px 16px;">
                  <span style="color: white; font-size: 20px; font-weight: 800; letter-spacing: 2px;">K-PROD</span>
                </div>
              </div>
              <div style="background: white; border-radius: 12px; padding: 28px; border: 1px solid #e2e8f0;">
                <h2 style="margin: 0 0 8px; font-size: 18px; color: #1e293b;">Code de vérification</h2>
                <p style="margin: 0 0 20px; font-size: 14px; color: #64748b;">
                  Utilisez ce code pour finaliser votre connexion. Il expire dans <strong>10 minutes</strong>.
                </p>
                <div style="text-align: center; background: #f1f5f9; border-radius: 10px; padding: 20px; margin: 20px 0; letter-spacing: 10px; font-size: 32px; font-weight: 800; color: #4f46e5;">
                  ${code}
                </div>
                <p style="margin: 0; font-size: 12px; color: #94a3b8; text-align: center;">
                  Si vous n'avez pas demandé ce code, ignorez cet email.
                </p>
              </div>
            </div>
          `,
          text: `Votre code de connexion K-PROD : ${code} (valable 10 minutes)`
        }
      });

      if (emailError) throw emailError;
      return { error: null };
    } catch (err: any) {
      console.error('Error sending email code:', err);
      return { error: 'Impossible d\'envoyer le code par email' };
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const { data: users, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (fetchError || !users) {
        return { error: 'Email ou mot de passe incorrect' };
      }

      const isValidPassword = await bcrypt.compare(password, users.password);
      if (!isValidPassword) {
        return { error: 'Email ou mot de passe incorrect' };
      }

      const userData: User = {
        id: users.id,
        email: users.email,
        firstname: users.firstname,
        lastname: users.lastname,
        agency: users.agency,
        role: users.role,
        email_2fa_enabled: users.email_2fa_enabled,
        totp_enabled: users.totp_enabled,
      };

      // TOTP 2FA (admin)
      if (users.role === 'admin' && users.totp_enabled) {
        setTempUserData(userData);
        setTempUserId(users.id);
        setTempUserEmail(users.email);
        return {
          error: null,
          requiresTwoFactor: true,
          secret: users.totp_secret
        };
      }

      // Email 2FA
      if (users.email_2fa_enabled) {
        setTempUserData(userData);
        setTempUserId(users.id);
        setTempUserEmail(users.email);

        const { error: sendError } = await sendEmailCode(users.id, users.email);
        if (sendError) return { error: sendError };

        return {
          error: null,
          requiresEmailCode: true,
          userEmail: users.email
        };
      }

      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return { error: null };
    } catch (error) {
      console.error('Login error:', error);
      return { error: 'Une erreur est survenue' };
    }
  };

  const verifyTwoFactor = async (token: string) => {
    if (!tempUserData) {
      return { error: 'Session expirée' };
    }
    setUser(tempUserData);
    localStorage.setItem('user', JSON.stringify(tempUserData));
    setTempUserData(null);
    setTempUserId(null);
    setTempUserEmail(null);
    return { error: null };
  };

  const verifyEmailCode = async (code: string): Promise<{ error: string | null }> => {
    if (!tempUserData || !tempUserId) {
      return { error: 'Session expirée, veuillez vous reconnecter' };
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('users')
        .select('email_2fa_code, email_2fa_code_expires_at')
        .eq('id', tempUserId)
        .single();

      if (fetchError || !data) {
        return { error: 'Erreur de vérification' };
      }

      if (!data.email_2fa_code || data.email_2fa_code !== code) {
        return { error: 'Code incorrect' };
      }

      if (!data.email_2fa_code_expires_at || new Date(data.email_2fa_code_expires_at) < new Date()) {
        return { error: 'Code expiré, veuillez en demander un nouveau' };
      }

      // Clear the code
      await supabase
        .from('users')
        .update({ email_2fa_code: null, email_2fa_code_expires_at: null })
        .eq('id', tempUserId);

      setUser(tempUserData);
      localStorage.setItem('user', JSON.stringify(tempUserData));
      setTempUserData(null);
      setTempUserId(null);
      setTempUserEmail(null);
      return { error: null };
    } catch (err: any) {
      return { error: 'Une erreur est survenue' };
    }
  };

  const resendEmailCode = async (): Promise<{ error: string | null }> => {
    if (!tempUserId || !tempUserEmail) {
      return { error: 'Session expirée, veuillez vous reconnecter' };
    }
    return sendEmailCode(tempUserId, tempUserEmail);
  };

  const logout = async () => {
    setUser(null);
    setTempUserData(null);
    setTempUserId(null);
    setTempUserEmail(null);
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyTwoFactor, verifyEmailCode, resendEmailCode, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
